import { useEffect, useRef, useState } from 'react'
import './formulario-publico.css'
import AssessmentFlow from './AssessmentFlow'
import ProvaTecnicaPublica from './ProvaTecnicaPublica'
import { NIVEIS_EXPERIENCIA } from './runtime/nivelExperiencia'
import { useNotification } from '../../context/NotificationContext'

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 2) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

// Com ?prova=<código> o mesmo endereço abre a prova técnica enviada pelo RH.
export default function FormularioPublicoPage() {
  const provaToken = new URLSearchParams(window.location.search).get('prova')
  return provaToken ? <ProvaTecnicaPublica token={provaToken} /> : <FormularioCandidato />
}

function FormularioCandidato() {
  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Veiga Advogados - Trabalhe conosco'

    return () => {
      document.title = previousTitle
    }
  }, [])

  const { showWarning } = useNotification()
  const [overlayOpen, setOverlayOpen] = useState(false)
  const [candidate, setCandidate] = useState({ name: '', email: '', phone: '', nivelExperiencia: '' })
  const [area, setArea] = useState('')
  const [consent, setConsent] = useState(false)

  const nameRef = useRef(null)
  const emailRef = useRef(null)
  const phoneRef = useRef(null)
  const areaRef = useRef(null)
  const nivelRef = useRef(null)
  const consentRef = useRef(null)

  const openAssessment = () => {
    const validations = [
      { valid: candidate.name.trim().length > 0, message: 'Preencha o nome completo.', ref: nameRef },
      { valid: candidate.email.trim().length > 0, message: 'Preencha o e-mail.', ref: emailRef },
      { valid: candidate.phone.replace(/\D/g, '').length >= 10, message: 'Preencha um telefone válido.', ref: phoneRef },
      { valid: area.trim().length > 0, message: 'Preencha a área pretendida.', ref: areaRef },
      { valid: Boolean(candidate.nivelExperiencia), message: 'Informe seu nível de experiência em advocacia.', ref: nivelRef },
      { valid: consent, message: 'Aceite os termos da LGPD para continuar.', ref: consentRef },
    ]

    const firstInvalid = validations.find((item) => !item.valid)
    if (firstInvalid) {
      showWarning(firstInvalid.message)
      firstInvalid.ref.current?.focus()
      firstInvalid.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setOverlayOpen(true)
  }

  return (
    <>
      <main className="page">
        <section className="branding">
          <h1>Talentos que<br />constroem o futuro.</h1>
          <p>Buscamos pessoas brilhantes, éticas e apaixonadas por excelência para transformar os desafios em grandes conquistas.</p>
        </section>

        <section className="panel-shell">
          <div className="panel">
            <svg className="curve primary" viewBox="0 0 170 1000" preserveAspectRatio="none" aria-hidden="true">
              <path d="M148 -6 C 86 78, 86 168, 116 286 C 147 416, 154 560, 118 702 C 96 790, 100 896, 145 1008" stroke="#2F73FF" strokeWidth="2.4" fill="none" opacity="0.95" />
            </svg>
            <svg className="curve secondary" viewBox="0 0 170 1000" preserveAspectRatio="none" aria-hidden="true">
              <path d="M158 -20 C 96 86, 92 182, 124 304 C 156 430, 160 568, 126 706 C 104 804, 112 914, 156 1028" stroke="#2F73FF" strokeWidth="1.6" fill="none" opacity="0.64" />
            </svg>

            <div className="panel-content">
              <div className="header">
                <img className="header-logo" src="/logo.png" alt="Veiga Advogados" />
                <div className="eyebrow">Recrutamento e Seleção</div>
                <h2>Trabalhe conosco</h2>
                <p>Veiga Advogados</p>
              </div>

              <div className="form-container">
                <div className="form-group"><label>Nome completo</label><input ref={nameRef} type="text" placeholder="Seu nome" value={candidate.name} onChange={(e)=>setCandidate((p)=>({...p,name:e.target.value}))} /></div>
                <div className="form-group"><label>E-mail</label><input ref={emailRef} type="email" placeholder="seu@email.com" value={candidate.email} onChange={(e)=>setCandidate((p)=>({...p,email:e.target.value}))} /></div>
                <div className="form-group"><label>Telefone</label><input ref={phoneRef} type="tel" placeholder="(11) 99999-0000" value={candidate.phone} onChange={(e)=>setCandidate((p)=>({...p,phone:formatPhone(e.target.value)}))} /></div>
                <div className="form-group"><label>Área pretendida</label><input ref={areaRef} type="text" placeholder="Ex.: Tributário consultivo, contencioso, compliance..." value={area} onChange={(e)=>setArea(e.target.value)} /></div>
                <div className="form-group">
                  <label htmlFor="nivel-experiencia">Nível de experiência em advocacia</label>
                  <select id="nivel-experiencia" ref={nivelRef} value={candidate.nivelExperiencia} onChange={(e)=>setCandidate((p)=>({...p,nivelExperiencia:e.target.value}))}>
                    <option value="" disabled>Selecione</option>
                    {NIVEIS_EXPERIENCIA.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>

                <div className="consent">
                  <input ref={consentRef} type="checkbox" id="consent" checked={consent} onChange={(e)=>setConsent(e.target.checked)} />
                  <label htmlFor="consent">Autorizo o tratamento dos meus dados pessoais para fins de recrutamento e seleção, nos termos da LGPD (Lei 13.709/2018).</label>
                </div>

                <button className="btn-secondary" type="button" onClick={openAssessment}>Iniciar questionário comportamental</button>
                <div className="disclaimer">Nesta etapa inicial, pedimos nome, e-mail, telefone, área pretendida e nível de experiência. Depois disso, o questionário começa direto.</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <div id="assessmentOverlay" className="assessment-overlay" hidden={!overlayOpen}>
        <div className="assessment-backdrop" onClick={() => setOverlayOpen(false)}></div>
        <div className="assessment-shell" role="dialog" aria-modal="true" aria-label="Avaliação comportamental">
          <button type="button" className="assessment-close" aria-label="Fechar avaliação" onClick={() => setOverlayOpen(false)}>×</button>
          <div className="assessment-app">
            <AssessmentFlow candidate={candidate} area={area} consent={consent} onClose={() => setOverlayOpen(false)} />
          </div>
        </div>
      </div>
    </>
  )
}
